import React from 'react';
import { Platform, View } from 'react-native';
// Note: do NOT import react-native-vector-icons at module top-level because
// bundlers like Vite will attempt to optimize those deps for web and fail
// when the package or native binding isn't available. We'll require them at
// runtime only on native platforms.

// For web builds we prefer react-icons (which is present in the web app
// workspace) instead of importing react-native-vector-icons which may not
// be installed or wired for web. Map common icon names to react-icons MD
// equivalents for a safe web fallback.
import { MdPlace, MdSearch, MdClose, MdTune, MdChevronRight, MdRestaurant, MdHotel, MdCalendarToday, MdShoppingCart, MdDirectionsTransit, MdDirectionsBus, MdDirectionsCar, MdDirectionsWalk, MdDirectionsBike, MdTwoWheeler, MdPhotoCamera, MdFavoriteBorder, MdChildCare, MdHistory, MdNature, MdAccountBalance, MdCardGiftcard, MdNightlightRound, MdStar, MdStarBorder, MdStarHalf, MdChurch, MdApartment, MdRestaurantMenu, MdLocalDining, MdFastfood, MdEco, MdVerified, MdRamenDining, MdCake, MdFreeBreakfast, MdHome, MdStorefront, MdBeachAccess, MdBusiness, MdFamilyRestroom, MdLocalHotel, MdDevices, MdCheckroom, MdPalette, MdLocalGroceryStore, MdFlight, MdPerson, MdPeople, MdTerrain, MdEvent, MdBuild, MdAdd, MdAddCircleOutline, MdNotifications, MdSettings, MdGroup, MdArrowBack, MdEdit, MdDone, MdDelete, MdDescription, MdInfo, MdLocationOn, MdNavigation, MdNearMe, MdMap, MdFullscreen, MdFullscreenExit, MdDirectionsBoat, MdPhone, MdCall } from 'react-icons/md';
import { SiGooglemaps } from 'react-icons/si';

export interface PlatformIconProps {
  name: string;
  size?: number;
  color?: string;
  // optional icon family override. Default is MaterialCommunityIcons for broader coverage.
  family?: 'MaterialCommunityIcons' | 'MaterialIcons';
}

// Default to MaterialCommunityIcons which has much larger coverage for travel/poi icons.
// Keep MaterialIcons available as an explicit family option for backward compatibility.
const PlatformIcon: React.FC<PlatformIconProps> = ({ name, size = 20, color = '#fff', family = 'MaterialCommunityIcons' }) => {
    // Web: use react-icons mapping
    if (Platform.OS === 'web') {
        const style = { width: size, height: size, color } as any;
        const createIcon = (IconComp: any, props: any) => React.createElement(IconComp, props);
        // Normalize incoming name to prefer hyphenated form so we match cases like
        // 'directions_transit' and 'directions-transit' equally.
        const normName = name ? String(name).replace(/_/g, '-').trim() : '';
        switch (normName) {
      case 'map-marker':
      case 'place':
        return createIcon(MdPlace, { style });
      case 'photo_camera':
        return createIcon(MdPhotoCamera, { style });
      case 'museum':
  return createIcon(MdAccountBalance, { style });
      case 'church':
  return createIcon(MdChurch, { style });
      case 'apartment':
  return createIcon(MdApartment, { style });
      case 'nature':
  return createIcon(MdNature, { style });
      case 'favorite-border':
      case 'couple':
  return createIcon(MdFavoriteBorder, { style });
      case 'child-care':
      case 'family-friendly':
  return createIcon(MdChildCare, { style });
      case 'history':
  return createIcon(MdHistory, { style });
      case 'card-giftcard':
      case 'free':
  return createIcon(MdCardGiftcard, { style });
      case 'nightlight-round':
      case 'night-view':
  return createIcon(MdNightlightRound, { style });
      case 'star':
  return createIcon(MdStar, { style });
      case 'star-border':
      case 'star-outline':
  return createIcon(MdStarBorder, { style });
      case 'star-half':
  return createIcon(MdStarHalf, { style });
      case 'magnify':
      case 'search':
  return createIcon(MdSearch, { style });
      case 'people':
  return createIcon(MdPeople, { style });
      case 'add':
        // prefer the plain plus icon (no circle)
  return createIcon(MdAdd, { style });
      case 'close':
  return createIcon(MdClose, { style });
      case 'tune':
  return createIcon(MdTune, { style });
      case 'chevron-right':
  return createIcon(MdChevronRight, { style });
      case 'arrow-back':
  return createIcon(MdArrowBack, { style });
      case 'delete':
  return createIcon(MdDelete, { style });
      case 'edit':
  return createIcon(MdEdit, { style });
      case 'location_on':
  return createIcon(MdLocationOn, { style });
      case 'navigation':
  return createIcon(MdNavigation, { style });
      case 'near_me':
  return createIcon(MdNearMe, { style });
            case 'fullscreen':
                return createIcon(MdFullscreen, { style });
            case 'fullscreen-exit':
            case 'fullscreen_exit':
                return createIcon(MdFullscreenExit, { style });
              case 'call':
              case 'phone':
              case 'local-phone':
                return createIcon(MdPhone, { style });
              case 'directions-car':
                        return createIcon(MdDirectionsCar, { style });
                    case 'directions-bus':
                    case 'directions-transit':
                        return createIcon(MdDirectionsBus, { style });
                    case 'directions-walk':
                        return createIcon(MdDirectionsWalk, { style });
                    case 'directions-bike':
                        return createIcon(MdDirectionsBike, { style });
                    // Common aliases that appear in payloads: map subway/boat/ferry to a generic transit icon
                    case 'directions-subway':
                        return createIcon(MdDirectionsTransit, { style });
                    case 'directions-boat':
                    case 'directions-ferry':
                    case 'directions-rail':
                        return createIcon(MdDirectionsBoat, { style });
                            case 'two_wheeler':
                            case 'two-wheeler':
                                return createIcon(MdTwoWheeler, { style });
                            // local taxi sometimes appears as local-taxi
                            case 'local-taxi':
                                return createIcon(MdDirectionsCar, { style });
                    case 'map':
                        return createIcon(MdMap, { style });
                    case 'google-maps':
                        return createIcon(SiGooglemaps, { style });
      case 'description':
  return createIcon(MdDescription, { style });
      case 'info':
  return createIcon(MdInfo, { style });
      case 'silverware-fork-knife':
      case 'restaurant':
  return createIcon(MdRestaurant, { style });
      case 'restaurant_menu':
  return createIcon(MdRestaurantMenu, { style });
      case 'local-dining':
  return createIcon(MdLocalDining, { style });
      case 'fastfood':
  return createIcon(MdFastfood, { style });
      case 'eco':
  return createIcon(MdEco, { style });
      case 'verified':
  return createIcon(MdVerified, { style });
      case 'ramen-dining':
  return createIcon(MdRamenDining, { style });
      case 'cake':
  return createIcon(MdCake, { style });
      case 'free-breakfast':
  return createIcon(MdFreeBreakfast, { style });
      case 'bus':
      case 'directions-transit':
  return createIcon(MdDirectionsTransit, { style });
      case 'bed':
      case 'hotel':
  return createIcon(MdHotel, { style });
      case 'calendar':
  return createIcon(MdCalendarToday, { style });
      case 'cart':
      case 'shopping-cart':
  return createIcon(MdShoppingCart, { style });
      case 'devices':
  return createIcon(MdDevices, { style });
      case 'checkroom':
  return createIcon(MdCheckroom, { style });
      case 'palette':
  return createIcon(MdPalette, { style });
      case 'local-grocery-store':
  return createIcon(MdLocalGroceryStore, { style });
      case 'flight':
  return createIcon(MdFlight, { style });
      case 'account-balance':
  return createIcon(MdAccountBalance, { style });
      case 'home':
  return createIcon(MdHome, { style });
      case 'person':
  return createIcon(MdPerson, { style });
      case 'group':
  return createIcon(MdGroup, { style });
      case 'notifications':
  return createIcon(MdNotifications, { style });
      case 'settings':
  return createIcon(MdSettings, { style });
      case 'add-circle-outline':
  return createIcon(MdAddCircleOutline, { style });
      case 'terrain':
  return createIcon(MdTerrain, { style });
      case 'event':
  return createIcon(MdEvent, { style });
      case 'build':
  return createIcon(MdBuild, { style });
      case 'storefront':
  return createIcon(MdStorefront, { style });
      case 'beach-access':
  return createIcon(MdBeachAccess, { style });
      case 'business':
  return createIcon(MdBusiness, { style });
      case 'family-restroom':
  return createIcon(MdFamilyRestroom, { style });
      case 'local-hotel':
  return createIcon(MdLocalHotel, { style });
      default:
        // If the requested icon wasn't explicitly mapped above, provide a
        // reasonable fallback. For MaterialIcons prefer the ligature font
        // rendering (so most Material icon names will work) — apps/web
        // includes the Material Icons stylesheet. Convert hyphens to
        // underscores which is the ligature naming convention.
                if (family === 'MaterialIcons') {
                    // Defensive: if name is missing or falsy, avoid calling replace and return empty placeholder
                    if (!normName) {
                        return <View style={{ width: size, height: size }} />;
                    }
                        // If we couldn't map the requested name to a react-icon above,
                        // render the ligature only as a last resort. Convert hyphens to
                        // underscores for the ligature convention.
                        const ligature = String(normName).replace(/-/g, '_');
                        // Debug: log when we fall back to ligature so we can trace which
                        // icon names are not mapped to SVG and still using font ligatures.
                        try { console.debug('[PlatformIcon] ligature fallback for', normName); } catch (e) {}
                    const spanStyle = { fontSize: size, width: size, height: size, color, display: 'inline-block', lineHeight: `${size}px`, textAlign: 'center' } as any;
                    return <span className="material-icons" style={spanStyle}>{ligature}</span>;
                }

        // fallback: return an empty view to avoid crash / bundler failures
        return <View style={{ width: size, height: size }} />;
    }
  }

  // Native: require react-native-vector-icons dynamically so web bundlers
  // won't attempt to resolve these modules.
    try {
      // require at runtime to avoid bundlers including native-only modules for web
      // accept either the default export or the module itself depending on packaging
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const mi = require('react-native-vector-icons/MaterialIcons');
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      const mci = require('react-native-vector-icons/MaterialCommunityIcons');
      const MaterialIcon = (mi && (mi.default || mi)) as any;
      const MaterialCommunityIcon = (mci && (mci.default || mci)) as any;

      if (family === 'MaterialIcons') {
        return <MaterialIcon name={name} size={size} color={color} />;
      }

      // Map common Material names to MaterialCommunityIcons equivalents
      let mciName = name;
      switch (name) {
        case 'notifications': mciName = 'bell'; break;
        case 'person': mciName = 'account'; break;
        case 'settings': mciName = 'cog'; break;
        case 'search': mciName = 'magnify'; break;
        case 'info': mciName = 'information'; break;
        case 'arrow-back': mciName = 'arrow-left'; break;
        case 'place': mciName = 'map-marker'; break;
        case 'location-on': mciName = 'map-marker'; break;
        case 'edit': mciName = 'pencil'; break;
        case 'delete': mciName = 'delete'; break; // delete is same
        case 'add': mciName = 'plus'; break;
        case 'close': mciName = 'close'; break; // close is same
        case 'check': mciName = 'check'; break; // check is same
        case 'chevron-right': mciName = 'chevron-right'; break; // same
        case 'favorite': mciName = 'heart'; break;
        case 'favorite-border': mciName = 'heart-outline'; break;
        case 'share': mciName = 'share-variant'; break;
        case 'star': mciName = 'star'; break; // same
        case 'more-vert': mciName = 'dots-vertical'; break;
        case 'more-horiz': mciName = 'dots-horizontal'; break;
        case 'tune': mciName = 'tune'; break; // same
        case 'view-grid': mciName = 'view-grid'; break; // same
        case 'view-list': mciName = 'view-list'; break; // same
      }

      return <MaterialCommunityIcon name={mciName} size={size} color={color} />;
    } catch (e) {
      // If require fails for any reason on native, return an empty view to avoid crash
      return <View style={{ width: size, height: size }} />;
    }
};

export default PlatformIcon;
export { PlatformIcon };
